<template>
  <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
  <EditableComponent
    v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
    :id="id"
    :duplicate="componentPermissions.canDuplicate"
    :excludeStylables="[ThemeComponentOptionEnum.PADDING]"
    :remove="componentPermissions.canRemove"
    :resizable="isScribbleChild && !usedInSectionId"
    :selected="componentSelectedMode && componentSelectedMode.id === id"
    :settings="!usedInSectionId"
    stylable
    :variants="
      componentPermissions.canEdit && !isEditModeDisabled
        ? {
            title: t('canvasUI.canvasBuilder.html.selectLayout'),
            options: displayOptions,
            selected: effectiveEditState === HtmlLayoutTypes.EDIT ? HtmlLayoutTypes.EDIT : HtmlLayoutTypes.PREVIEW,
          }
        : undefined
    "
    @duplicate="duplicate(id)"
    @remove="removeComponentById(id)"
    @select="setComponentSelectedMode(id)"
    @settings="setComponentSettingsMode(id)"
    @style="$emit('style', $event)"
    @variant-change="onVariantChange"
  >
    <div class="cb-html relative">
      <div
        v-if="
          !pitcherSettings.enable_monaco_html_editor &&
          effectiveEditState === HtmlLayoutTypes.EDIT &&
          componentPermissions.canEdit
        "
        class="relative"
      >
        <div v-if="launchDarkly.allow_ai_prompts_in_canvas_text" class="absolute top-2 right-2 z-10">
          <CAiHtmlActions
            :model-value="data?.html ?? ''"
            :theme-overrides="aiThemeOverrides"
            @end-loading="isAiLoading = false"
            @start-loading="isAiLoading = true"
            @update:model-value="(v) => updateNodeDataById(id, { html: v })"
          />
        </div>
        <NInput
          class="cb-html__textarea mt-2 no-border"
          :placeholder="t('canvasUI.canvasBuilder.html.pasteYourHtml')"
          :style="{
            '--n-border': 'none',
            '--n-border-disabled': 'none',
            '--n-border-hover': 'none',
            '--n-border-focus': 'none',
            '--n-padding-left': '16px',
            '--n-padding-right': '16px',
            fontFamily: 'var(--font-family)',
          }"
          type="textarea"
          :value="data?.html ?? ''"
          @focus="onFocus"
          @keydown.stop
          @keyup.stop
          @update-value="updateNodeDataById(id, { html: $event })"
        />
      </div>
      <div
        v-else-if="
          pitcherSettings.enable_monaco_html_editor &&
          effectiveEditState === HtmlLayoutTypes.EDIT &&
          componentPermissions.canEdit
        "
        class="cb-html__monaco-container relative overflow-hidden rounded-md"
        :style="{ height: `${monacoHeight}px` }"
      >
        <div v-if="launchDarkly.allow_ai_prompts_in_canvas_text" class="absolute top-2 right-2 z-10">
          <CAiHtmlActions
            :model-value="data?.html ?? ''"
            :theme-overrides="aiThemeOverrides"
            @end-loading="isAiLoading = false"
            @start-loading="isAiLoading = true"
            @update:model-value="(v) => updateNodeDataById(id, { html: v })"
          />
        </div>
        <CMonacoEditor
          class="rounded-md"
          :height="monacoHeight"
          :model-value="data?.html ?? ''"
          :placeholder="t('canvasUI.canvasBuilder.html.pasteYourHtml')"
          @focus="onFocus"
          @save="() => saveCanvasContent({ showToast: false })"
          @update:model-value="(v) => updateNodeDataById(id, { html: v })"
        />
        <div
          class="absolute bottom-10px right-10px w-20px h-20px bg-transparent cursor-nw-resize before:content-empty before:absolute before:bottom-2px before:right-2px before:w-12px before:h-2px before:bg-info before:rounded-1px after:content-empty after:absolute after:bottom-2px after:right-2px after:w-2px after:h-12px after:bg-info after:rounded-1px"
          @mousedown="startResize"
        />
      </div>
      <div
        v-else-if="pitcherSettings.canvas?.enable_v_trusted_js_in_html"
        ref="contentElement"
        v-trusted-html="getModelValue(data?.html)"
        class="pa-3"
      />
      <div v-else ref="contentElement" class="pa-3" v-html="getModelValue(data?.html)" />

      <div
        v-if="
          isEditMode &&
          (!componentSelectedMode || componentSelectedMode.id !== id) &&
          getModelValue(data?.html ?? '').includes('<iframe')
        "
        class="absolute top-0 left-0 w-full h-full"
      />
    </div>
  </EditableComponent>

  <div
    v-else-if="pitcherSettings.canvas?.enable_v_trusted_js_in_html"
    ref="contentElement"
    v-trusted-html="getModelValue(data?.html)"
  />
  <div v-else ref="contentElement" v-html="getModelValue(data?.html)" />
</template>

<script setup lang="ts">
import { h, ref, toRefs, onMounted, onBeforeUnmount, inject, computed, type ComputedRef } from 'vue'
import { NInput, type DropdownOption } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import { renderTemplate } from '@lib/util/handlebars'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import type { HtmlProps, ComponentNode } from '@canvas-builder/types/canvas'
import { ComponentTypes } from '@canvas-builder/types/canvas'
import { HtmlLayoutTypes } from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import type { CanvasRetrieve } from '@openapi'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import { ThemeComponentOptionEnum } from '@canvas-builder/composables/useCanvasTheme'
import usePrintMode from '@canvas-builder/composables/usePrintMode'
import CMonacoEditor from '@lib/components/CMonacoEditor/CMonacoEditor.vue'
import CAiHtmlActions from './CAiHtmlActions.vue'
import { vTrustedHtml } from '@lib/directives/vTrustedHtml'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { PitcherInfo } from '@lib/types/app'

const props = withDefaults(
  defineProps<{
    id: string
    data?: HtmlProps
    when_used_in_section?: ComponentNode['when_used_in_section']
    usedInSectionId?: CanvasRetrieve['id']
    visible?: ComponentNode['visible']
    tracking_id?: string
    parentType?: ComponentTypes
  }>(),
  {
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)

const { id, usedInSectionId, when_used_in_section } = toRefs(props)
const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const { t } = useI18n()
const editState = ref<HtmlLayoutTypes>(props.data?.html ? HtmlLayoutTypes.PREVIEW : HtmlLayoutTypes.EDIT)
const contentElement = ref<HTMLElement | null>(null)
const monacoHeight = ref<number>(400)
const isAiLoading = ref(false)

const aiThemeOverrides = computed(() => ({
  default: {},
  active: {},
}))

const {
  mode,
  isEditMode,
  removeComponentById,
  updateNodeDataById,
  setComponentSelectedMode,
  componentSelectedMode,
  duplicate,
  activeCanvas,
  isViewOnlyMode,
  setComponentSettingsMode,
  isPrintDisplayMode,
  saveCanvasContent,
} = useCanvas()

const { applyPrintModeToElement } = usePrintMode()

const isScribbleChild = computed(() => {
  return props.parentType === ComponentTypes.Scribble
})

const displayOptions = [
  {
    label: t('canvasUI.canvasBuilder.html.preview'),
    key: HtmlLayoutTypes.PREVIEW,
    icon: () => h(CIcon, { icon: 'desktop', faType: 'fas', size: '16px' }),
  },
  {
    label: t('canvasUI.canvasBuilder.html.htmlMode'),
    key: HtmlLayoutTypes.EDIT,
    icon: () => h(CIcon, { icon: 'code', size: '16px' }),
  },
]

// Force preview mode when edit is not available
const isEditModeDisabled = computed(
  () => launchDarkly.value?.show_only_preview_mode_for_html_component && !pitcherInfo.value?.isAnyTypeOfAdmin,
)

// Update edit state when edit mode is disabled
const effectiveEditState = computed(() => {
  if (isEditModeDisabled.value) {
    return HtmlLayoutTypes.PREVIEW
  }
  return editState.value
})

function onFocus() {
  setComponentSelectedMode(props.id)
}

function onVariantChange(selection?: DropdownOption['key']) {
  const variant = selection as HtmlLayoutTypes

  if (componentPermissions.value.canEdit) {
    editState.value = variant
    setComponentSelectedMode(props.id)
  }
}

const shouldInterpolate = computed((): boolean => {
  if (!activeCanvas.value?.context) return false
  else if (activeCanvas.value?.is_template || activeCanvas.value?.is_section) return !isEditMode.value
  return launchDarkly.value?.interpolate_rep_canvases_on_edit_mode || !isEditMode.value
})

function getModelValue(value = '') {
  if (!shouldInterpolate.value || !value.trim() || !activeCanvas.value?.context) return value

  return renderTemplate(value, activeCanvas.value.context)
}

// Resize state
let startY = 0
let startHeight = 0

// Resize handlers
function onMouseMove(e: MouseEvent) {
  const deltaY = e.clientY - startY
  const newHeight = Math.max(200, Math.min(1200, startHeight + deltaY))
  monacoHeight.value = newHeight
}

function onMouseUp() {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
}

function startResize(event: MouseEvent) {
  event.preventDefault()

  // Clean up any existing listeners first
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)

  startY = event.clientY
  startHeight = monacoHeight.value

  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
}

onMounted(() => {
  if (isPrintDisplayMode.value && contentElement.value) {
    setTimeout(() => {
      if (contentElement.value) {
        applyPrintModeToElement(contentElement.value, () => {
          return getModelValue(props.data?.html || '')
        })
      }
    }, 100)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})
</script>

<style lang="scss" scoped>
.editable-component {
  :deep(.n-input__textarea-el) {
    font-family: monospace;
    font-size: 14px;
  }
}

.cb-html__textarea :deep(textarea) {
  min-height: 300px;
}
</style>
